import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { Textarea } from '@/components/ui-kit/textarea';
import { Question } from '../../types/form-engine.types';
import { clearQuestionAnswer } from '../../utils/question-factory';

export interface QuestionAnswerFieldProps {
  question: Question;
  onChange: (question: Question) => void;
  readOnly?: boolean;
  /** Allow the reviewer to wipe their answer (legacy: only after the deadline, before completion). */
  canClear?: boolean;
}

const range = (min: number, max: number) =>
  Array.from({ length: Math.max(max - min + 1, 0) }, (_, i) => min + i);

const hasAnswer = (question: Question) => {
  switch (question.FormatType) {
    case 'short-text':
    case 'long-text':
    case 'date':
      return Boolean(question.Answer);
    case 'multiple-choice':
      return Boolean(question.McOptionsValue);
    case 'checkboxes':
      return Boolean(question.CbOptionsValues?.length);
    case 'rating':
    case 'nps':
      return Boolean(question.Rating);
    case 'likert-scale':
      return (question.LikertStatements ?? []).some((statement) => statement.Value.length > 0);
    default:
      return false;
  }
};

export const QuestionAnswerField = ({
  question,
  onChange,
  readOnly,
  canClear,
}: QuestionAnswerFieldProps) => {
  const { t } = useTranslation();
  const patch = (changes: Partial<Question>) => onChange({ ...question, ...changes });
  const selectedCheckboxes = question.CbOptionsValues ?? [];
  const options = question.Options ?? [];
  const likertLabels = question.LikertLabels ?? [];
  const likertStatements = question.LikertStatements ?? [];

  return (
    <div className="space-y-3 rounded-md border p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Label>
            {question.Question}
            {question.IsRequired && <span className="text-error"> *</span>}
          </Label>
          {question.QuestionDescription && (
            <p className="mt-1 text-xs text-muted-foreground">{question.QuestionDescription}</p>
          )}
        </div>
        {canClear && !readOnly && hasAnswer(question) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(clearQuestionAnswer(question))}
          >
            {t('CLEAR_ANSWER')}
          </Button>
        )}
      </div>

      {question.FormatType === 'short-text' && (
        <Input
          disabled={readOnly}
          value={question.Answer ?? ''}
          onChange={(e) => patch({ Answer: e.target.value })}
        />
      )}

      {question.FormatType === 'long-text' && (
        <Textarea
          rows={3}
          disabled={readOnly}
          value={question.Answer ?? ''}
          onChange={(e) => patch({ Answer: e.target.value })}
        />
      )}

      {question.FormatType === 'date' && (
        <DatePicker
          disabled={readOnly}
          value={question.Answer ? question.Answer.split('T')[0] : ''}
          onChange={(value) => patch({ Answer: value })}
        />
      )}

      {question.FormatType === 'multiple-choice' && (
        <RadioGroup
          value={question.McOptionsValue ?? ''}
          onValueChange={(next) => patch({ McOptionsValue: next })}
          className="flex flex-col gap-2"
        >
          {options.map((option) => (
            <label key={option.Value} className="flex min-h-9 items-center gap-2 text-sm">
              <RadioGroupItem value={option.Value} disabled={readOnly} />
              {option.Label}
            </label>
          ))}
        </RadioGroup>
      )}

      {question.FormatType === 'checkboxes' && (
        <div className="flex flex-col gap-2">
          {options.map((option) => (
            <label key={option.Value} className="flex min-h-9 items-center gap-2 text-sm">
              <Checkbox
                disabled={readOnly}
                checked={selectedCheckboxes.includes(option.Value)}
                onCheckedChange={(checked) =>
                  patch({
                    CbOptionsValues: checked
                      ? [...selectedCheckboxes, option.Value]
                      : selectedCheckboxes.filter((value) => value !== option.Value),
                  })
                }
              />
              {option.Label}
            </label>
          ))}
        </div>
      )}

      {(question.FormatType === 'rating' || question.FormatType === 'nps') && (
        <div className="space-y-1">
          <div className="flex flex-wrap gap-2">
            {range(question.MinRating, question.MaxRating).map((value) => (
              <Button
                key={value}
                type="button"
                size="sm"
                disabled={readOnly}
                variant={question.Rating === value ? 'default' : 'outline'}
                onClick={() => patch({ Rating: value })}
              >
                {value}
              </Button>
            ))}
          </div>
          {(question.MinRatingLabel || question.MaxRatingLabel) && (
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{question.MinRatingLabel}</span>
              <span>{question.MaxRatingLabel}</span>
            </div>
          )}
        </div>
      )}

      {question.FormatType === 'likert-scale' && (
        <>
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead />
                  {likertLabels.map((label) => (
                    <TableHead key={label} className="text-center">
                      {label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {likertStatements.map((statement, statementIndex) => (
                  <TableRow key={statement.Statement}>
                    <TableCell>{statement.Statement}</TableCell>
                    {likertLabels.map((label) => (
                      <TableCell key={label} className="text-center">
                        <input
                          type="radio"
                          disabled={readOnly}
                          name={`${question.ItemId}-${statementIndex}`}
                          checked={statement.Value[0] === label}
                          onChange={() =>
                            patch({
                              LikertStatements: likertStatements.map((s, i) =>
                                i === statementIndex ? { ...s, Value: [label] } : s
                              ),
                            })
                          }
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="space-y-3 md:hidden">
            {likertStatements.map((statement, statementIndex) => (
              <div
                key={`${statement.Statement}-${statementIndex}`}
                role="group"
                aria-label={statement.Statement}
                className="space-y-1 rounded-md border p-3"
              >
                <p className="px-1 text-sm font-medium">{statement.Statement}</p>
                {likertLabels.map((label) => (
                  <label key={label} className="flex min-h-9 items-center gap-2 text-sm">
                    <input
                      type="radio"
                      className="size-4 shrink-0"
                      disabled={readOnly}
                      name={`${question.ItemId}-m-${statementIndex}`}
                      checked={statement.Value[0] === label}
                      onChange={() =>
                        patch({
                          LikertStatements: likertStatements.map((s, i) =>
                            i === statementIndex ? { ...s, Value: [label] } : s
                          ),
                        })
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
            ))}
          </div>
        </>
      )}

      {question.HasComment && (
        <Textarea
          rows={2}
          disabled={readOnly}
          placeholder={t('COMMENT')}
          value={question.Comments ?? ''}
          onChange={(e) => patch({ Comments: e.target.value })}
        />
      )}
    </div>
  );
};
